/**
 * Dashboard merchant i18n — 5 lingue (it default), react-i18next.
 * Priorità lingua: localStorage `carthub_dashboard_locale` → navigator → it.
 * Varianti browser (es. en-GB, de-AT) mappate sulla base ISO a 2 lettere.
 *
 * Plurali: i18next nativi (count + suffissi _zero/_one/_other dove serve).
 * Namespace `emails`: solo copy UI in dashboard (timing/anteprime); testi email inviati dal backend = altra epic.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import commonIt from "./locales/it/common.json";
import commonEn from "./locales/en/common.json";
import commonDe from "./locales/de/common.json";
import commonFr from "./locales/fr/common.json";
import commonEs from "./locales/es/common.json";

import cartsIt from "./locales/it/carts.json";
import cartsEn from "./locales/en/carts.json";
import cartsDe from "./locales/de/carts.json";
import cartsFr from "./locales/fr/carts.json";
import cartsEs from "./locales/es/carts.json";

import settingsIt from "./locales/it/settings.json";
import settingsEn from "./locales/en/settings.json";
import settingsDe from "./locales/de/settings.json";
import settingsFr from "./locales/fr/settings.json";
import settingsEs from "./locales/es/settings.json";

import emailsIt from "./locales/it/emails.json";
import emailsEn from "./locales/en/emails.json";
import emailsDe from "./locales/de/emails.json";
import emailsFr from "./locales/fr/emails.json";
import emailsEs from "./locales/es/emails.json";

import statsIt from "./locales/it/stats.json";
import statsEn from "./locales/en/stats.json";
import statsDe from "./locales/de/stats.json";
import statsFr from "./locales/fr/stats.json";
import statsEs from "./locales/es/stats.json";

import brandsIt from "./locales/it/brands.json";
import brandsEn from "./locales/en/brands.json";
import brandsDe from "./locales/de/brands.json";
import brandsFr from "./locales/fr/brands.json";
import brandsEs from "./locales/es/brands.json";

import priceAlertsIt from "./locales/it/priceAlerts.json";
import priceAlertsEn from "./locales/en/priceAlerts.json";
import priceAlertsDe from "./locales/de/priceAlerts.json";
import priceAlertsFr from "./locales/fr/priceAlerts.json";
import priceAlertsEs from "./locales/es/priceAlerts.json";

import priceAlertSetupIt from "./locales/it/priceAlertSetup.json";
import priceAlertSetupEn from "./locales/en/priceAlertSetup.json";
import priceAlertSetupDe from "./locales/de/priceAlertSetup.json";
import priceAlertSetupFr from "./locales/fr/priceAlertSetup.json";
import priceAlertSetupEs from "./locales/es/priceAlertSetup.json";

import subscriptionIt from "./locales/it/subscription.json";
import subscriptionEn from "./locales/en/subscription.json";
import subscriptionDe from "./locales/de/subscription.json";
import subscriptionFr from "./locales/fr/subscription.json";
import subscriptionEs from "./locales/es/subscription.json";

import trialIt from "./locales/it/trial.json";
import trialEn from "./locales/en/trial.json";
import trialDe from "./locales/de/trial.json";
import trialFr from "./locales/fr/trial.json";
import trialEs from "./locales/es/trial.json";

import onsiteIt from "./locales/it/onsite.json";
import onsiteEn from "./locales/en/onsite.json";
import onsiteDe from "./locales/de/onsite.json";
import onsiteFr from "./locales/fr/onsite.json";
import onsiteEs from "./locales/es/onsite.json";

export const LOCALE_STORAGE_KEY = "carthub_dashboard_locale";

/** Lingue canoniche dashboard (v1). */
export const SUPPORTED_LOCALES = ["it", "en", "de", "fr", "es"];

/** Normalizza codice lingua (navigator o salvato) su una delle lingue supportate. */
export function normalizeLocale(raw) {
  if (!raw || typeof raw !== "string") return "it";
  const base = raw.split("-")[0].toLowerCase();
  return SUPPORTED_LOCALES.includes(base) ? base : "it";
}

/** `navigator.language` → locale BCP-47 per date (sidebar, ecc.). */
export function dateLocaleForAppLang(lang) {
  const map = { it: "it-IT", en: "en-GB", de: "de-DE", fr: "fr-FR", es: "es-ES" };
  return map[normalizeLocale(lang)] || "it-IT";
}

/** Importo con ISO 4217 e locale dashboard (fallback EUR). */
export function formatMoney(lang, currencyCode, n) {
  const code =
    currencyCode != null && String(currencyCode).trim() !== ""
      ? String(currencyCode).trim().toUpperCase()
      : "EUR";
  const loc = dateLocaleForAppLang(lang);
  const num = Number(n) || 0;
  try {
    return new Intl.NumberFormat(loc, { style: "currency", currency: code }).format(num);
  } catch {
    return `${code} ${num.toFixed(2)}`;
  }
}

/** @deprecated Usare formatMoney(lang, 'EUR', n) o la valuta negozio. */
export function formatEur(lang, n) {
  return formatMoney(lang, "EUR", n);
}

/** Tempo relativo (lista carrelli, ecc.) — usa namespace `common` (chiavi `timeAgo.*`). */
export function formatAgo(iso) {
  if (!iso) return "";
  const t = i18n.getFixedT(i18n.language, "common");
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return t("timeAgo.lessThanHour");
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return t("timeAgo.days", { count: d });
  if (h > 0) return t("timeAgo.hours", { count: h });
  return t("timeAgo.lessThanHour");
}

export function getInitialLanguage() {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) {
      const n = normalizeLocale(stored);
      if (SUPPORTED_LOCALES.includes(n)) return n;
    }
  } catch (_) {}
  try {
    return normalizeLocale(navigator.language || navigator.userLanguage || "it");
  } catch (_) {
    return "it";
  }
}

const bundle = (c, ca, s, e, st, br, pa, pas, su, tr, on) => ({
  common: c,
  carts: ca,
  settings: s,
  emails: e,
  stats: st,
  brands: br,
  priceAlerts: pa,
  priceAlertSetup: pas,
  subscription: su,
  trial: tr,
  onsite: on,
});

const resources = {
  it: bundle(commonIt, cartsIt, settingsIt, emailsIt, statsIt, brandsIt, priceAlertsIt, priceAlertSetupIt, subscriptionIt, trialIt, onsiteIt),
  en: bundle(commonEn, cartsEn, settingsEn, emailsEn, statsEn, brandsEn, priceAlertsEn, priceAlertSetupEn, subscriptionEn, trialEn, onsiteEn),
  de: bundle(commonDe, cartsDe, settingsDe, emailsDe, statsDe, brandsDe, priceAlertsDe, priceAlertSetupDe, subscriptionDe, trialDe, onsiteDe),
  fr: bundle(commonFr, cartsFr, settingsFr, emailsFr, statsFr, brandsFr, priceAlertsFr, priceAlertSetupFr, subscriptionFr, trialFr, onsiteFr),
  es: bundle(commonEs, cartsEs, settingsEs, emailsEs, statsEs, brandsEs, priceAlertsEs, priceAlertSetupEs, subscriptionEs, trialEs, onsiteEs),
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: "it",
  defaultNS: "common",
  ns: ["common", "carts", "settings", "emails", "stats", "brands", "priceAlerts", "priceAlertSetup", "subscription", "trial", "onsite"],
  interpolation: { escapeValue: false },
});

i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalizeLocale(lng));
  } catch (_) {}
});

export default i18n;
